import React, { memo } from 'react';

const TIPOS_COMPROBANTE_GASTO = [
    'Factura A',
    'Factura B',
    'Factura C',
    'Impuestos / Tasas Fiscales',
    'Préstamo',
    'Ticket Fiscal',
    'Sin Comprobante'
];

const TIPOS_GASTO = ['Fijos', 'Variables'];

const calcularPeriodo = (fechaString) => {
    if (!fechaString) return '';
    const dateObj = new Date(fechaString);
    if (isNaN(dateObj.getTime())) return '';
    const m = String(dateObj.getMonth() + 1).padStart(2, '0');
    const y = dateObj.getFullYear();
    return `${m}/${y}`;
};

const SeccionComprobanteProveedor = ({
    formData,
    setFormData,
    proveedores = [],
    areas = [],
    conceptosGastos = [],
    subconceptosDisponibles = [],
    handleProveedorChange,
    handleAreaChange,
    handleConceptoChange
}) => {
    const handleFechaGastoChange = (e) => {
        const val = e.target.value;
        const periodo = calcularPeriodo(val);
        setFormData(prev => ({ ...prev, fechaGasto: val, periodo: periodo || prev.periodo }));
    };

    return (
        <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm transition-all duration-300">
            <legend className="px-3 text-lg font-bold text-slate-700 bg-white absolute -top-3.5 left-4 tracking-tight">
                Comprobante y Proveedor
            </legend>

            <div className="space-y-4 pt-2">
                {/* 1er LÍNEA: FECHAS */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Fecha de Ingreso *</label>
                        <input
                            type="date"
                            required
                            value={formData.fechaIngreso || ''}
                            onChange={e => setFormData(prev => ({ ...prev, fechaIngreso: e.target.value }))}
                            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Fecha de Gasto *</label>
                        <input
                            type="date"
                            required
                            value={formData.fechaGasto || ''}
                            onChange={handleFechaGastoChange}
                            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-[14px] font-bold text-rose-800 mb-1.5 pl-1">Fecha Vencimiento *</label>
                        <input
                            type="date"
                            required
                            value={formData.fechaVencimientoGasto || ''}
                            onChange={e => setFormData(prev => ({ ...prev, fechaVencimientoGasto: e.target.value }))}
                            className="w-full border border-rose-200 bg-rose-50/20 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-900 outline-none h-[46px] focus:border-rose-400 focus:ring-1 focus:ring-rose-100 transition-all"
                        />
                    </div>
                </div>

                {/* 2da LÍNEA: TIPO COMPROBANTE / N° COMPROBANTE */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Tipo Comprobante *</label>
                        <select
                            value={formData.tipoComprobante || ''}
                            onChange={e => setFormData(prev => ({ ...prev, tipoComprobante: e.target.value }))}
                            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                            required
                        >
                            <option value="">Seleccionar...</option>
                            {TIPOS_COMPROBANTE_GASTO.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">N° Comprobante</label>
                        <input
                            type="text"
                            value={formData.numeroComprobante || ''}
                            onChange={e => setFormData(prev => ({ ...prev, numeroComprobante: e.target.value }))}
                            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-mono font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                            placeholder="0001-00005678, VEP o Préstamo N°"
                        />
                    </div>
                </div>

                {/* 3ra LÍNEA: PROVEEDOR / FISCO / ÁREA */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Proveedor / Fisco *</label>
                        <select
                            value={formData.proveedorId || ''}
                            onChange={handleProveedorChange}
                            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                            required
                        >
                            <option value="">Seleccionar...</option>
                            {proveedores.map(p => (
                                <option key={p.id} value={p.id}>{p.razonSocial || p.nombre} {p.cuit ? `(${p.cuit})` : ''}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Área</label>
                        <select
                            value={formData.areaId || ''}
                            onChange={handleAreaChange}
                            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                        >
                            <option value="">Seleccionar...</option>
                            {areas.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                        </select>
                    </div>
                </div>

                {/* 4ta LÍNEA: TIPO DE GASTO / CONCEPTO / SUBCONCEPTO */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Tipo de Gasto *</label>
                        <select
                            value={formData.tipoGasto || ''}
                            onChange={e => setFormData(prev => ({ ...prev, tipoGasto: e.target.value }))}
                            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                        >
                            <option value="">Seleccionar...</option>
                            {TIPOS_GASTO.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>

                    <div>
                        <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Concepto *</label>
                        <select
                            value={formData.conceptoId || ''}
                            onChange={handleConceptoChange}
                            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                            required
                        >
                            <option value="">Seleccionar...</option>
                            {conceptosGastos.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                        </select>
                    </div>

                    <div>
                        <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Subconcepto</label>
                        <select
                            value={formData.subconceptoNombre || ''}
                            onChange={e => setFormData(prev => ({ ...prev, subconceptoNombre: e.target.value }))}
                            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all disabled:opacity-60"
                            disabled={subconceptosDisponibles.length === 0}
                        >
                            <option value="">
                                {subconceptosDisponibles.length === 0 ? 'Sin Subconceptos' : 'Seleccionar...'}
                            </option>
                            {subconceptosDisponibles.map(sub => <option key={sub} value={sub}>{sub}</option>)}
                        </select>
                    </div>
                </div>
            </div>
        </fieldset>
    );
};

export default memo(SeccionComprobanteProveedor);